// Push-based minimum-supported-version gate.
//
// The routine new-build path (useVersionCheck + UpdateAvailableNotification)
// reacts on tab focus / a 10-min poll — there's no way to evict every live
// client off a bad build in seconds. This gate rides the maintenance-flag
// Firestore listener (useMaintenanceFlag): ops writes a `minSupportedVersion`
// onto `app_config/maintenance`, and any live client whose running build is
// below it hard-reloads to fetch the current index.html.
//
// Version strings come from the build-baked VITE_APP_VERSION, which CI stamps
// from `git describe --tags` — e.g. `v1.63.0`, `v1.63.0-3-gabc1234` (3 commits
// past the tag), or a bare sha when no tag is reachable. We compare on the
// semver core only; the `-N-gsha` describe suffix and the cache-version
// timestamp are irrelevant to "is this build at least version X".

const RELOAD_GUARD_KEY = "min-version-reload-attempted";

/**
 * Extract [major, minor, patch] from a version string, or null when no semver
 * core is present (e.g. a bare `git describe --always` sha, or undefined).
 * Tolerates a leading `v` and any suffix (`-N-gsha`, `-<timestamp>`).
 */
export const parseSemver = (version) => {
  if (typeof version !== "string") return null;
  const match = version.match(/(\d+)\.(\d+)\.(\d+)/);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};

/**
 * True when `running` is strictly below `minSupported` on semver core.
 * Fails open: if either side has no parseable semver, returns false (no gate).
 */
export const isBelowMinVersion = (running, minSupported) => {
  const cur = parseSemver(running);
  const min = parseSemver(minSupported);
  if (!cur || !min) return false;
  for (let i = 0; i < 3; i++) {
    if (cur[i] < min[i]) return true;
    if (cur[i] > min[i]) return false;
  }
  return false; // equal → at the gate, not below
};

/**
 * If the running build is below `minSupported`, hard-reload once per distinct
 * gate value. Keying the guard on the min-version (not a time cooldown) means a
 * reload that lands on a build still below the gate — deploy not yet propagated
 * — won't loop; a later ops bump to a higher min re-arms the reload.
 */
export const maybeForceReloadForMinVersion = (running, minSupported) => {
  if (!isBelowMinVersion(running, minSupported)) return;
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === minSupported) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, minSupported);
  } catch {
    // sessionStorage unavailable — fall through and reload anyway.
  }
  window.location.reload();
};
